import { format, parse } from 'date-fns';
import { it } from 'date-fns/locale/it';
import { fr } from 'date-fns/locale/fr';
import { es } from 'date-fns/locale/es';
import { de } from 'date-fns/locale/de';
import { pt } from 'date-fns/locale/pt';
import { nl } from 'date-fns/locale/nl';
import { pl } from 'date-fns/locale/pl';
import { el } from 'date-fns/locale/el';
import { sv } from 'date-fns/locale/sv';
import { da } from 'date-fns/locale/da';
import { nb } from 'date-fns/locale/nb';
import { hi } from 'date-fns/locale/hi';
import { ja } from 'date-fns/locale/ja';
import { zhCN } from 'date-fns/locale/zh-CN';
import type { Locale } from 'date-fns';

export type DateFormat = 'full' | 'elegant' | 'medium' | 'minimal';

export const DATE_FORMATS: { id: DateFormat; label: string }[] = [
  { id: 'full',    label: 'Full' },
  { id: 'elegant', label: 'Elegant' },
  { id: 'medium',  label: 'Medium' },
  { id: 'minimal', label: 'Minimal' },
];

const FNS_LOCALES: Record<string, Locale> = {
  it, fr, es, de, pt, nl, pl, el, sv, da, no: nb, hi, ja, 'zh-CN': zhCN,
};

// Locales that use year-month-day structure with native characters (年月日).
// These need completely different format strings from the Western patterns.
const CJK_DATE_LOCALES = new Set(['ja', 'zh-CN']);

/**
 * Format a YYYY-MM-DD event date string into a display string.
 * Parsed as local date to avoid timezone day-shift.
 *
 * full:    "Saturday, June 15th, 2026"
 * elegant: "Saturday, the 15th of June, 2026"
 * medium:  "June 15th, 2026"
 * minimal: "15 · 06 · 26"
 */
export function formatEventDate(dateStr: string, fmt: DateFormat): string {
  const date = parse(dateStr, 'yyyy-MM-dd', new Date());
  switch (fmt) {
    case 'full':
      return format(date, "EEEE, MMMM do, yyyy");
    case 'elegant':
      return `${format(date, 'EEEE')}, the ${format(date, 'do')} of ${format(date, 'MMMM, yyyy')}`;
    case 'medium':
      return format(date, "MMMM do, yyyy");
    case 'minimal':
      return format(date, "dd · MM · yy");
    default:
      return format(date, "EEEE, MMMM do, yyyy");
  }
}

/**
 * Like formatEventDate but uses the guest's locale for language-native day/month names.
 * Italian example: "venerdì 18 settembre 2026" (no capitals, no commas).
 * Falls back to English formatting when no locale or locale is 'en'.
 */
export function formatLocalizedDate(dateStr: string, fmt: DateFormat, locale?: string): string {
  if (!locale || locale === 'en') return formatEventDate(dateStr, fmt);

  const fnsLocale = FNS_LOCALES[locale];
  if (!fnsLocale) return formatEventDate(dateStr, fmt);

  const date = parse(dateStr, 'yyyy-MM-dd', new Date());

  if (CJK_DATE_LOCALES.has(locale)) {
    // ja:    2025年6月15日（日曜日） / 2025年6月15日 / 6月15日 / 15·06·25
    // zh-CN: 2025年6月15日星期日    / 2025年6月15日 / 6月15日 / 15·06·25
    const weekday = locale === 'ja'
      ? `（${format(date, 'EEEE', { locale: fnsLocale })}）`
      : format(date, 'EEEE', { locale: fnsLocale });
    switch (fmt) {
      case 'minimal':  return format(date, 'dd · MM · yy');
      case 'medium':   return format(date, 'M月d日', { locale: fnsLocale });
      case 'elegant':  return format(date, 'yyyy年M月d日', { locale: fnsLocale });
      case 'full':
      default:         return `${format(date, 'yyyy年M月d日', { locale: fnsLocale })}${weekday}`;
    }
  }

  switch (fmt) {
    case 'minimal':
      return format(date, 'dd · MM · yy');
    case 'medium':
      return format(date, 'd MMMM yyyy', { locale: fnsLocale });
    case 'elegant':
      return format(date, 'd MMMM yyyy', { locale: fnsLocale });
    case 'full':
    default:
      return format(date, 'EEEE d MMMM yyyy', { locale: fnsLocale });
  }
}
